import { useQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { PageHeader } from '@/components/admin/AdminShell';
import { MetaBox } from '@/components/admin/MetaBox';
import { Notice } from '@/components/admin/Notice';
import { StepFlow } from '@/components/admin/StepFlow';
import { createRecurringEvents, PARTICIPATION_FORMAT_LABELS, type EventRecurringCreateInput } from '@/features/events/api';
import { EventNotificationPlanPreview } from '@/features/events/EventNotificationPlanPreview';
import { fetchEventTypes } from '@/features/masters/api';
import { ApiError } from '@/lib/api/http';
import { datetimeLocalToIso } from '@/lib/datetime';

const EMPTY: EventRecurringCreateInput = {
  type_id: '',
  title: '',
  first_starts_at: '',
  duration_minutes: 120,
  venue: '',
  meet_url: '',
  participation_format: 'both',
  meet_log_enabled: null,
  interval: 'monthly',
  occurrences: 3,
};

/** イベントの繰返し作成（指示書 §4「作成: 個別、繰返し、CSV一括」）. */
export function EventRecurringCreatePage(): ReactElement {
  const navigate = useNavigate();
  const eventTypes = useQuery({ queryKey: ['event-types'], queryFn: fetchEventTypes });
  const [values, setValues] = useState<EventRecurringCreateInput>(EMPTY);
  const [firstStartsAtInput, setFirstStartsAtInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [createdCount, setCreatedCount] = useState<number | null>(null);

  async function onSubmit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      const events = await createRecurringEvents({
        ...values,
        first_starts_at: datetimeLocalToIso(firstStartsAtInput),
        venue: values.venue || null,
        meet_url: values.meet_url || null,
        meet_log_enabled: Boolean(values.meet_log_enabled),
      });
      setCreatedCount(events.length);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '登録に失敗しました。');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <PageHeader title="イベントの繰返し作成" description="下書きとして複数のイベントを一度に作成します。" />
      <StepFlow
        steps={[
          { label: '繰り返す条件を決める', detail: '種別・題名・開始日・繰返し間隔・回数。まとめて下書きが作られます' },
          { label: '1件ずつ対象者を決める', detail: 'イベント一覧から1件ずつ開いて「対象者」タブで設定します' },
          { label: '1件ずつ公開する', detail: '公開したものだけが受講生に見えます。メールの予約は作成時に入っています（公開前は送らずスキップ）' },
        ]}
        currentIndex={0}
        note="ここで作られるのはすべて下書きです。対象者の設定と公開は、作成後に1件ずつ行います。"
      />
      <MetaBox title="設定">
        {error && (
          <Notice kind="error" dismissible={false}>
            {error}
          </Notice>
        )}
        {createdCount !== null && (
          <Notice kind="success" dismissible={false}>
            {createdCount}件のイベントを下書きとして作成しました。
          </Notice>
        )}
        {eventTypes.isError && (
          <Notice kind="error" dismissible={false}>
            イベント種別の取得に失敗しました。
            <button
              type="button"
              onClick={() => void eventTypes.refetch()}
              className="ml-2 underline"
            >
              再試行
            </button>
          </Notice>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-wptext-muted">
              種別 <span className="text-wpnotice-error">*</span>
            </span>
            <select
              value={values.type_id}
              onChange={(e) => setValues({ ...values, type_id: e.target.value })}
              className="w-full rounded-sm border border-wpborder px-3 py-2"
            >
              <option value="">選択してください</option>
              {(eventTypes.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-wptext-muted">
              タイトル <span className="text-wpnotice-error">*</span>
            </span>
            <input
              value={values.title}
              onChange={(e) => setValues({ ...values, title: e.target.value })}
              className="w-full rounded-sm border border-wpborder px-3 py-2"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">
              初回開始日時 <span className="text-wpnotice-error">*</span>
            </span>
            <input
              type="datetime-local"
              value={firstStartsAtInput}
              onChange={(e) => setFirstStartsAtInput(e.target.value)}
              className="w-full rounded-sm border border-wpborder px-3 py-2"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">所要時間（分）</span>
            <input
              type="number"
              min={0}
              value={values.duration_minutes}
              onChange={(e) =>
                setValues({ ...values, duration_minutes: Number(e.target.value) })
              }
              className="w-full rounded-sm border border-wpborder px-3 py-2"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">間隔</span>
            <select
              value={values.interval}
              onChange={(e) =>
                setValues({ ...values, interval: e.target.value as 'weekly' | 'monthly' })
              }
              className="w-full rounded-sm border border-wpborder px-3 py-2"
            >
              <option value="weekly">毎週</option>
              <option value="monthly">毎月</option>
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">開催回数（2〜52）</span>
            <input
              type="number"
              min={2}
              max={52}
              value={values.occurrences}
              onChange={(e) => setValues({ ...values, occurrences: Number(e.target.value) })}
              className="w-full rounded-sm border border-wpborder px-3 py-2"
            />
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-wptext-muted">
              参加形式 <span className="text-wpnotice-error">*</span>
            </span>
            {(
              [
                ['offline', PARTICIPATION_FORMAT_LABELS.offline],
                ['online', PARTICIPATION_FORMAT_LABELS.online],
                ['both', PARTICIPATION_FORMAT_LABELS.both],
              ] as const
            ).map(([value, label]) => (
              <label key={value} className="mr-4 inline-flex items-center gap-1">
                <input
                  type="radio"
                  name="participation_format"
                  checked={values.participation_format === value}
                  onChange={() => setValues({ ...values, participation_format: value })}
                />
                {label}
              </label>
            ))}
            <p className="mt-1 text-wptext-muted">
              両方を選ぶと、受講生は現地参加かオンライン参加かを選べます。
            </p>
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-wptext-muted">
              参加ログ <span className="text-wpnotice-error">*</span>
            </span>
            <span className="mr-4 inline-flex items-center gap-1">
              <input
                type="radio"
                name="meet_log_enabled"
                checked={values.meet_log_enabled === true}
                onChange={() => setValues({ ...values, meet_log_enabled: true })}
              />
              取る
            </span>
            <span className="mr-4 inline-flex items-center gap-1">
              <input
                type="radio"
                name="meet_log_enabled"
                checked={values.meet_log_enabled === false}
                onChange={() => setValues({ ...values, meet_log_enabled: false })}
              />
              取らない
            </span>
            {values.meet_log_enabled === true && (
              <p className="mt-1 text-wptext-muted">
                参加ログは、Meetを発行した<strong>主催者のGoogleアカウント</strong>
                でしかAPIから取得できません。運営で使う指定のGoogleアカウントで会議を発行してください。個人アカウントで発行した会議のログは取得できません。
              </p>
            )}
          </label>
        </div>
      </MetaBox>
      {values.type_id && (
        <EventNotificationPlanPreview
          typeId={values.type_id}
          startsAt={firstStartsAtInput}
          endsAt=""
        />
      )}
      <MetaBox title="作成">
        <div className="flex gap-2">
          <button
            type="button"
            disabled={
              submitting ||
              !values.type_id ||
              !values.title ||
              !firstStartsAtInput ||
              values.meet_log_enabled === null
            }
            onClick={() => void onSubmit()}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            {submitting ? '作成中…' : `${values.occurrences}件作成する`}
          </button>
          <button
            type="button"
            onClick={() => navigate('/events')}
            className="btn-secondary"
          >
            一覧に戻る
          </button>
        </div>
      </MetaBox>
    </>
  );
}
